import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Button } from "@/components/ui/baseUi/button"
import { DropdownMenuItem } from "@/components/ui/baseUi/dropdown-menu"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import type { BoxSessionFormikEntity } from "@/modules/boxSlotSession/entities/boxSessionFormikEntity"
import { cn } from "@/modules/lib/utils"
import type {
  ProgramInfiniteSessionFormikType,
  ProgramOnOffSessionFormikType,
  ProgramSessionInputKey,
} from "@/modules/sessions/programSessionFormikType"
import type { CoachNoteFormikType } from "@/modules/sessions/sessionForm/blocks/noteBlock/noteFormikTypes"
import { useBlockDuration } from "@/modules/sessions/sessionForm/hooks/useBlockDuration"
import { useSessionSidebar } from "@/modules/sessions/sessionForm/hooks/useSessionSidebar"
import { type SessionBlockCoachNoteFormikType } from "@/modules/sessions/sessionForm/sessionBlockFormikTypes"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import type { SimpleFormik } from "@/typings/formik"
import { ArrowIcon } from "@images/svgIcons/arrowIcon"
import { ChevronIcon } from "@images/svgIcons/chevronIcon"
import { CoachingTipsIcon } from "@images/svgIcons/coachingTipsIcon"
import { EditIcon } from "@images/svgIcons/editIcon"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { word } from "@modules/core/utils/i18n"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { HURichTextView } from "@modules/ui/components/richText/huRichTextView"
import { formatTextToRichText } from "@modules/ui/components/richText/richTextUtils"
import { useState } from "react"

type Props = {
  formik:
    | SimpleFormik<BoxSessionFormikEntity, ProgramSessionInputKey>
    | SimpleFormik<ProgramInfiniteSessionFormikType, ProgramSessionInputKey>
    | SimpleFormik<ProgramOnOffSessionFormikType, ProgramSessionInputKey>
  block: SessionBlockCoachNoteFormikType
  moveBlock: (blockIndex: number, direction: "UP" | "DOWN") => Promise<void>
  deleteBlock: (blockIndex: number) => Promise<void>
  blockIndex: number
  className?: string
} & React.ComponentProps<"div">

export function NoteForCoachesExistingBloc({
  formik,
  block,
  moveBlock,
  deleteBlock,
  blockIndex,
  className,
  ...props
}: Props) {
  const { theme } = useTheme()
  const [isContentDisplayed, setIsContentDisplayed] = useState(true)

  const { hideSidebarStackOnTop } = useSidebar()
  const { navigateToNoteForCoachesForm } = useSessionSidebar()
  const { getBlockDurationInterval } = useBlockDuration(formik.values.blocks)

  return (
    <div className={cn(className)} {...props}>
      {!!block.content.durationInMin && (
        <Text fontStyle="BS" className="mb-1 flex flex-row items-center gap-2">
          <div className="bg-neutral-700" style={{ width: 10, height: 10, borderRadius: 3 }} />
          {getBlockDurationInterval(blockIndex)}
        </Text>
      )}

      <div
        className={
          "flex flex-col rounded-3xl border border-borderDefault p-3 pt-2 pl-1 " +
          (theme === Theme.OwnerDark ? "bg-neutral-50" : "")
        }
      >
        {/* Note card header */}
        <div className="flex flex-row items-center justify-between gap-1">
          <div className="flex flex-row items-center justify-start">
            <CoachingTipsIcon color="currentColor" className="text-textDefault-body" />
            <div className="flex flex-col">
              <Text fontStyle="TS" className="text-textDefault-body">
                {word("sessionForm.blocks.note_for_coaches")}
              </Text>
              <Text fontStyle="LM" className="text-textDefault-body">
                {block.content.noteType && word(`program.note.noteType.${block.content.noteType}`)}
              </Text>
            </div>
          </div>

          <Button variant="ghost" size="iconMd" onClick={() => setIsContentDisplayed(!isContentDisplayed)}>
            <ChevronIcon color="currentColor" className={isContentDisplayed ? "rotate-180" : "rotate-0"} />
          </Button>
        </div>

        {isContentDisplayed && (
          <>
            {/* Note card content */}
            <div style={{ paddingLeft: 50 }}>
              <HURichTextView value={formatTextToRichText(block.content.note ?? "")} paragrapheFontStyle="BS" />
            </div>

            {/* Note card footer*/}
            <div className="mt-2 flex flex-row items-center justify-end gap-1 pr-5">
              <EllipsisMenu propsContent={{ side: "bottom" }}>
                {blockIndex !== 0 && formik.values.blocks.length >= 1 && (
                  <DropdownMenuItem key="moveUp" onClick={() => void moveBlock(blockIndex, "UP")}>
                    <ArrowIcon
                      arrowDirection="top"
                      color={blockIndex === 0 ? "var(--neutral-300)" : "var(--neutral-900)"}
                      width={16}
                      height={16}
                    />
                    {word("sessionForm.blocks.move.up")}
                  </DropdownMenuItem>
                )}
                {blockIndex < formik.values.blocks.length - 1 && formik.values.blocks.length >= 1 && (
                  <DropdownMenuItem key="moveDown" onClick={() => void moveBlock(blockIndex, "DOWN")}>
                    <ArrowIcon
                      arrowDirection="bottom"
                      color={
                        blockIndex === formik.values.blocks.length - 1 ? "var(--neutral-300)" : "var(--neutral-900)"
                      }
                      width={16}
                      height={16}
                    />
                    {word("sessionForm.blocks.move.down")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  key="deleteBlock"
                  onClick={() => {
                    void deleteBlock(blockIndex)
                  }}
                >
                  <TrashIcon color="var(--neutral-900)" width={20} height={20} />
                  {word("sessionForm.exercise.remove_exo.button")}
                </DropdownMenuItem>
              </EllipsisMenu>

              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="primary"
                      size="iconMd"
                      onClick={() =>
                        navigateToNoteForCoachesForm(block.content, blockIndex, async (note: CoachNoteFormikType) => {
                          await formik.setFieldValue(
                            "blocks",
                            formik.values.blocks.map((block, index) =>
                              index === blockIndex ? { ...block, content: note } : block,
                            ),
                          )
                          hideSidebarStackOnTop()
                        })
                      }
                    >
                      <EditIcon color="currentColor" width={16} height={16} className="text-textDefault-heading" />
                    </Button>
                  }
                />
                <TooltipContent side="bottom">
                  <Text fontStyle="LM" className="text-textDefault-body">
                    {word("global.edit")}
                  </Text>
                </TooltipContent>
              </Tooltip>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
